import { useState } from 'react'
import { ArrowDownLeft, ArrowUpRight, Clock, CheckCircle, XCircle, Wallet as WalletIcon, TrendingUp, TrendingDown, Shield, AlertTriangle, ChevronDown } from 'lucide-react'
import { cn, formatRelativeTime } from '@/lib/utils'
import { motion, AnimatePresence } from 'framer-motion'
import { pageContainer, fadeInUp, cardReveal, listItem, tapScale } from '@/lib/motion'
import { Pagination, usePagination } from '@/components/shared/Pagination'
import { EmptyState } from '@/components/shared/EmptyState'
import { SkeletonList } from '@/components/shared/SkeletonLoader'
import { Drawer } from 'vaul'
import { useToast } from '@/components/shared/Toast'
import { useCollapsibleHeader } from '@/hooks/useCollapsibleHeader'

// Expanded mock transaction data for pagination demo
const mockTransactions = [
  { id: '1', type: 'deposit', amount: 10000, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 30) },
  { id: '2', type: 'win', amount: 4520, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 60 * 2) },
  { id: '3', type: 'loss', amount: -1500, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 60 * 3) },
  { id: '4', type: 'withdraw', amount: -5000, status: 'pending', date: new Date(Date.now() - 1000 * 60 * 60 * 5) },
  { id: '5', type: 'win', amount: 8900, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 60 * 24) },
  { id: '6', type: 'commission', amount: 350, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2) },
  { id: '7', type: 'deposit', amount: 25000, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3) },
  { id: '8', type: 'win', amount: 12500, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3 + 1000 * 60 * 60 * 2) },
  { id: '9', type: 'loss', amount: -3200, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 4) },
  { id: '10', type: 'withdraw', amount: -10000, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5) },
  { id: '11', type: 'win', amount: 6750, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5 + 1000 * 60 * 60 * 4) },
  { id: '12', type: 'commission', amount: 820, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 6) },
  { id: '13', type: 'loss', amount: -4500, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7) },
  { id: '14', type: 'deposit', amount: 15000, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7 + 1000 * 60 * 60 * 6) },
  { id: '15', type: 'win', amount: 22000, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 8) },
]

const depositPresets = [5000, 10000, 25000, 50000, 100000] // in cents
const withdrawPresets = [5000, 10000, 25000, 50000]

const txFilterTabs = [
  { label: '全部', value: '全部' },
  { label: '存款', value: '存款', type: 'deposit' },
  { label: '提款', value: '提款', type: 'withdraw' },
  { label: '盈利', value: '盈利', type: 'win' },
  { label: '虧損', value: '虧損', type: 'loss' },
  { label: '佣金', value: '佣金', type: 'commission' },
] as const

const getTransactionIcon = (type: string) => {
  switch (type) {
    case 'deposit':
      return <ArrowDownLeft className="w-5 h-5 text-emerald-400" />
    case 'withdraw':
      return <ArrowUpRight className="w-5 h-5 text-muted-foreground" />
    case 'win':
      return <TrendingUp className="w-5 h-5 text-gold" />
    case 'loss':
      return <TrendingDown className="w-5 h-5 text-muted-foreground" />
    case 'commission':
      return <WalletIcon className="w-5 h-5 text-purple-400" />
    default:
      return null
  }
}

const getTransactionLabel = (type: string) => {
  switch (type) {
    case 'deposit': return '存款'
    case 'withdraw': return '提款'
    case 'win': return '盈利'
    case 'loss': return '虧損'
    case 'commission': return '佣金'
    default: return type
  }
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'completed':
      return <CheckCircle className="w-4 h-4 text-emerald-500" />
    case 'pending':
      return <Clock className="w-4 h-4 text-gold" />
    case 'failed':
      return <XCircle className="w-4 h-4 text-rose-500" />
    default:
      return null
  }
}

export function WalletScreen() {
  const { toast } = useToast()
  const { headerRef, isMinimized } = useCollapsibleHeader()

  // Drawer states
  const [depositOpen, setDepositOpen] = useState(false)
  const [withdrawOpen, setWithdrawOpen] = useState(false)

  // Deposit flow
  const [depositAmount, setDepositAmount] = useState(0)
  const [depositStep, setDepositStep] = useState<'amount' | 'confirm' | 'processing' | 'success'>('amount')

  // Withdraw flow
  const [withdrawAmount, setWithdrawAmount] = useState(0)
  const [withdrawStep, setWithdrawStep] = useState<'amount' | 'confirm' | 'processing' | 'success'>('amount')

  // Demo toggles
  const [showEmptyDemo, setShowEmptyDemo] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // Transaction filters
  const [txFilter, setTxFilter] = useState('全部')
  const [expandedTx, setExpandedTx] = useState<string | null>(null)

  // Filter transactions
  const filteredTransactions = txFilter === '全部'
    ? mockTransactions
    : mockTransactions.filter((tx) => {
        const tab = txFilterTabs.find((t) => t.value === txFilter)
        return tab && 'type' in tab && tx.type === tab.type
      })

  const {
    currentPage,
    pageSize,
    totalPages,
    totalItems,
    paginatedItems: paginatedTransactions,
    setCurrentPage,
    setPageSize,
  } = usePagination(filteredTransactions, 5)

  const handleFilterChange = (value: string) => {
    setTxFilter(value)
    setCurrentPage(1)
  }

  const resetDeposit = () => {
    setDepositAmount(0)
    setDepositStep('amount')
  }

  const resetWithdraw = () => {
    setWithdrawAmount(0)
    setWithdrawStep('amount')
  }

  const simulateDeposit = () => {
    setDepositStep('processing')
    setTimeout(() => {
      setDepositStep('success')
      toast({ type: 'success', title: '存款成功', description: `$${(depositAmount / 100).toFixed(2)} 已到帳` })
    }, 2000)
  }

  const simulateWithdraw = () => {
    setWithdrawStep('processing')
    setTimeout(() => {
      setWithdrawStep('success')
      toast({ type: 'success', title: '提款申請已提交', description: `$${(withdrawAmount / 100).toFixed(2)} 預計 24 小時內到帳` })
    }, 2000)
  }

  return (
    <motion.div
      variants={pageContainer}
      initial="hidden"
      animate="visible"
      className="flex flex-col min-h-full bg-background relative overflow-x-hidden"
    >
      {/* Ambient glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[300px] gradient-radial-gold pointer-events-none" />
      <div className="fixed inset-0 noise-overlay pointer-events-none" />

      {/* Expanded Header */}
      <motion.header ref={headerRef} variants={fadeInUp} className="flex-shrink-0 px-6 pt-6 pb-2 relative z-10">
        <h1 className="font-display text-3xl font-semibold text-foreground mb-6">我的錢包</h1>

        {/* Balance Card */}
        <motion.div
          variants={cardReveal}
          className="relative p-6 rounded-3xl overflow-hidden glass-card-elevated mb-6"
        >
          <div className="absolute inset-0 rounded-3xl p-[1px] bg-gradient-to-r from-gold/0 via-gold/50 to-gold/0 opacity-50" />
          <div className="absolute inset-[1px] rounded-3xl bg-gradient-to-br from-surface-elevated to-surface-sunken" />
          <div className="absolute top-0 right-0 w-40 h-40 bg-gold/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />

          <div className="relative z-10">
            <p className="text-sm font-medium text-muted-foreground mb-1">總資產 (Total Balance)</p>
            <div className="flex items-baseline gap-2 mb-6">
              <span className="text-3xl font-bold text-gold">$</span>
              <span className="font-display text-5xl font-semibold text-foreground tracking-tight">1,250.00</span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-2xl bg-foreground/[0.03] border border-foreground/5">
                <p className="text-xs text-muted-foreground mb-1.5 uppercase tracking-wide">可用餘額</p>
                <p className="text-xl font-bold font-mono text-foreground">$1,050.00</p>
              </div>
              <div className="p-4 rounded-2xl bg-foreground/[0.03] border border-foreground/5">
                <p className="text-xs text-muted-foreground mb-1.5 uppercase tracking-wide">遊戲中</p>
                <p className="text-xl font-bold font-mono text-gold/80">$200.00</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div variants={fadeInUp} className="grid grid-cols-2 gap-4 mb-2">
          <motion.button
            whileTap={tapScale}
            onClick={() => setDepositOpen(true)}
            className="h-14 btn-primary rounded-2xl font-bold text-lg flex items-center justify-center gap-2"
          >
            <ArrowDownLeft className="w-5 h-5" />
            存款
          </motion.button>
          <motion.button
            whileTap={tapScale}
            onClick={() => setWithdrawOpen(true)}
            className="h-14 glass-card hover:border-gold/30 rounded-2xl font-bold text-lg text-foreground flex items-center justify-center gap-2 transition-colors duration-200"
          >
            <ArrowUpRight className="w-5 h-5 text-muted-foreground" />
            提款
          </motion.button>
        </motion.div>
      </motion.header>

      {/* Minimized sticky bar */}
      <div className="sticky top-0 z-40 h-0">
        <AnimatePresence>
          {isMinimized && (
            <motion.div
              initial={{ y: -48, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -48, opacity: 0 }}
              transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
              className="px-6 py-2 flex items-center justify-between bg-background/95 backdrop-blur-xl border-b border-foreground/5"
            >
              <div className="flex items-center gap-3">
                <span className="font-display text-sm font-semibold text-foreground">我的錢包</span>
                <span className="text-sm font-mono font-bold text-gold">$1,250.00</span>
              </div>
              <div className="flex items-center gap-2">
                <motion.button whileTap={tapScale} onClick={() => setDepositOpen(true)} className="px-3 py-1.5 btn-primary rounded-lg text-xs font-bold">存款</motion.button>
                <motion.button whileTap={tapScale} onClick={() => setWithdrawOpen(true)} className="px-3 py-1.5 glass-card rounded-lg text-xs font-bold text-foreground">提款</motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Transactions */}
      <motion.main
        variants={pageContainer}
        className="px-6 pb-24 relative z-10"
      >
        <motion.div variants={fadeInUp} className="flex items-center justify-between mb-4 mt-2">
          <h2 className="font-display text-xl font-semibold text-foreground">交易紀錄</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setIsLoading(!isLoading); if (!isLoading) setShowEmptyDemo(false) }}
              className={cn("text-[10px] font-mono px-2 py-1 rounded-lg transition-all border border-dashed", isLoading ? "bg-rose-500/10 text-rose-400 border-rose-500/30" : "bg-transparent text-muted-foreground/40 border-foreground/10")}
            >
              [DEV] 載入中
            </button>
            <button
              onClick={() => { setShowEmptyDemo(!showEmptyDemo); if (!showEmptyDemo) setIsLoading(false) }}
              className={cn("text-[10px] font-mono px-2 py-1 rounded-lg transition-all border border-dashed", showEmptyDemo ? "bg-rose-500/10 text-rose-400 border-rose-500/30" : "bg-transparent text-muted-foreground/40 border-foreground/10")}
            >
              [DEV] 空狀態
            </button>
            <button className="text-xs font-semibold text-gold/70 hover:text-gold transition-colors duration-200">查看全部</button>
          </div>
        </motion.div>

        {/* Transaction Type Filter Tabs */}
        <motion.div variants={fadeInUp} className="flex gap-2 overflow-x-auto scrollbar-hide pb-3 mb-3">
          {txFilterTabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => handleFilterChange(tab.value)}
              className={cn(
                "flex-shrink-0 px-4 py-2 rounded-full text-xs font-bold transition-all duration-200 border",
                txFilter === tab.value
                  ? "bg-gold/20 text-gold border-gold/30"
                  : "glass-card text-muted-foreground border-transparent hover:text-foreground"
              )}
            >
              {tab.label}
            </button>
          ))}
        </motion.div>

        {isLoading ? (
          <SkeletonList rows={5} />
        ) : showEmptyDemo ? (
          <EmptyState variant="no-transactions" />
        ) : filteredTransactions.length === 0 ? (
          <EmptyState
            variant="no-transactions"
            action={{
              label: '清除篩選',
              onClick: () => handleFilterChange('全部'),
            }}
          />
        ) : (
          <>
            {/* Pending Alert */}
            <motion.div
              variants={cardReveal}
              className="mb-4 p-4 rounded-2xl glass-card border-gold/30 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-gold" />
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground">提款處理中</p>
                  <p className="text-xs text-muted-foreground">預計 24 小時內到帳</p>
                </div>
              </div>
              <span className="text-lg font-bold font-mono text-gold">$50.00</span>
            </motion.div>

            <div className="space-y-3">
              {paginatedTransactions.map((tx) => (
                <motion.div
                  variants={listItem}
                  key={tx.id}
                  onClick={() => setExpandedTx(expandedTx === tx.id ? null : tx.id)}
                  className="glass-card rounded-2xl cursor-pointer group overflow-hidden"
                >
                  <div className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-4">
                      <div className={cn(
                        "w-12 h-12 flex items-center justify-center rounded-full border transition-colors duration-200",
                        tx.type === 'win' ? "bg-gold/10 border-gold/20 group-hover:border-gold/40" : "bg-surface-elevated border-foreground/5 group-hover:border-foreground/10"
                      )}>
                        {getTransactionIcon(tx.type)}
                      </div>
                      <div>
                        <p className="font-semibold text-foreground">{getTransactionLabel(tx.type)}</p>
                        <p className="text-xs text-muted-foreground font-medium">{formatRelativeTime(tx.date)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className={cn(
                          'font-bold font-mono text-lg',
                          tx.amount > 0 ? 'text-foreground' : 'text-muted-foreground',
                          tx.type === 'win' && 'text-gold'
                        )}>
                          {tx.amount > 0 ? '+' : ''}{(tx.amount / 100).toFixed(2)}
                        </p>
                        <div className="flex items-center justify-end gap-1.5 opacity-60">
                          {getStatusIcon(tx.status)}
                          <span className="text-[10px] text-muted-foreground">
                            {tx.status === 'completed' ? '完成' : tx.status === 'pending' ? '處理中' : '失敗'}
                          </span>
                        </div>
                      </div>
                      <ChevronDown className={cn(
                        "w-4 h-4 text-muted-foreground transition-transform duration-200",
                        expandedTx === tx.id && "rotate-180"
                      )} />
                    </div>
                  </div>

                  {/* Expanded details */}
                  <AnimatePresence>
                    {expandedTx === tx.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="border-t border-foreground/5"
                      >
                        <div className="p-4 bg-surface-sunken/50 space-y-2 text-xs">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">交易 ID</span>
                            <span className="font-mono text-foreground">TXN-{tx.id.padStart(6, '0')}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">類型</span>
                            <span className="text-foreground">{getTransactionLabel(tx.type)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">狀態</span>
                            <span className={cn(
                              tx.status === 'completed' ? 'text-emerald-400' : tx.status === 'pending' ? 'text-gold' : 'text-rose-400'
                            )}>
                              {tx.status === 'completed' ? '完成' : tx.status === 'pending' ? '處理中' : '失敗'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">時間</span>
                            <span className="text-foreground">{tx.date.toLocaleString('zh-TW')}</span>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            <div className="pt-4 pb-20">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                totalItems={totalItems}
                onPageChange={setCurrentPage}
                onPageSizeChange={setPageSize}
                pageSizeOptions={[5, 10, 20]}
              />
            </div>
          </>
        )}
      </motion.main>

      {/* ─── Deposit Drawer ─── */}
      <Drawer.Root open={depositOpen} onOpenChange={(open) => { setDepositOpen(open); if (!open) resetDeposit() }}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none">
            <div className="bg-surface-elevated rounded-t-3xl border-t border-foreground/10 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-center py-3">
                <div className="w-10 h-1.5 rounded-full bg-foreground/20" />
              </div>

              <div className="px-6 pb-8 safe-area-bottom">
                <AnimatePresence mode="wait">
                  {depositStep === 'amount' && (
                    <motion.div
                      key="amount"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                    >
                      <Drawer.Title className="font-display text-2xl font-semibold text-foreground mb-1">存款</Drawer.Title>
                      <p className="text-sm text-muted-foreground mb-6">選擇或輸入存款金額</p>

                      {/* Preset amounts */}
                      <div className="grid grid-cols-3 gap-2 mb-4">
                        {depositPresets.map((preset) => (
                          <button
                            key={preset}
                            onClick={() => setDepositAmount(preset)}
                            className={cn(
                              "py-3 rounded-xl font-bold text-sm transition-all",
                              depositAmount === preset
                                ? "bg-gold/20 text-gold border border-gold/30"
                                : "glass-card text-foreground hover:border-gold/20"
                            )}
                          >
                            ${(preset / 100).toFixed(0)}
                          </button>
                        ))}
                      </div>

                      <div className="mt-4 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                        <span className="text-xs text-emerald-400 font-medium">免手續費存款</span>
                      </div>

                      <button
                        onClick={() => depositAmount >= 1000 && setDepositStep('confirm')}
                        disabled={depositAmount < 1000}
                        className={cn(
                          "w-full h-14 rounded-2xl font-bold text-lg mt-6 transition-all duration-200",
                          depositAmount >= 1000
                            ? "btn-primary"
                            : "bg-surface-elevated text-muted-foreground/50 cursor-not-allowed border border-foreground/5"
                        )}
                      >
                        繼續
                      </button>
                    </motion.div>
                  )}

                  {depositStep === 'confirm' && (
                    <motion.div
                      key="confirm"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                    >
                      <Drawer.Title className="font-display text-2xl font-semibold text-foreground mb-1">確認存款</Drawer.Title>
                      <p className="text-sm text-muted-foreground mb-6">請確認以下存款資訊</p>

                      <div className="p-4 glass-card rounded-2xl mb-4 space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">存款金額</span>
                          <span className="text-sm font-bold text-foreground">${(depositAmount / 100).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">手續費</span>
                          <span className="text-sm font-bold text-emerald-400">免費</span>
                        </div>
                        <div className="border-t border-foreground/5 pt-3 flex justify-between">
                          <span className="text-sm font-bold text-foreground">實際到帳</span>
                          <span className="text-lg font-bold text-gold">${(depositAmount / 100).toFixed(2)}</span>
                        </div>
                      </div>

                      <div className="p-3 rounded-xl bg-gold/5 border border-gold/20 flex items-center gap-2 mb-6">
                        <Shield className="w-4 h-4 text-gold flex-shrink-0" />
                        <span className="text-xs text-gold font-medium">所有交易均受 SSL 加密保護</span>
                      </div>

                      <div className="flex gap-3">
                        <button
                          onClick={() => setDepositStep('amount')}
                          className="flex-1 h-14 glass-card rounded-2xl font-bold text-foreground hover:bg-foreground/5 transition-colors"
                        >
                          返回
                        </button>
                        <button
                          onClick={simulateDeposit}
                          className="flex-1 h-14 btn-primary rounded-2xl font-bold text-lg"
                        >
                          確認存款
                        </button>
                      </div>
                    </motion.div>
                  )}

                  {depositStep === 'processing' && (
                    <motion.div
                      key="processing"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="py-12 text-center"
                    >
                      <Drawer.Title className="sr-only">處理中</Drawer.Title>
                      <div className="w-16 h-16 rounded-full border-4 border-gold/20 border-t-gold animate-spin mx-auto mb-6" />
                      <p className="text-lg font-semibold text-foreground">處理中...</p>
                      <p className="text-sm text-muted-foreground mt-1">請稍候</p>
                    </motion.div>
                  )}

                  {depositStep === 'success' && (
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="py-8 text-center"
                    >
                      <Drawer.Title className="sr-only">存款成功</Drawer.Title>
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                        className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6"
                      >
                        <CheckCircle className="w-10 h-10 text-emerald-400" />
                      </motion.div>
                      <p className="text-lg font-semibold text-foreground mb-1">存款成功</p>
                      <p className="text-3xl font-bold text-gold mb-1">${(depositAmount / 100).toFixed(2)}</p>
                      <p className="text-sm text-muted-foreground mb-6">已到帳至您的錢包</p>
                      <button
                        onClick={() => { setDepositOpen(false); resetDeposit() }}
                        className="w-full h-14 btn-primary rounded-2xl font-bold text-lg"
                      >
                        完成
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>

      {/* ─── Withdraw Drawer ─── */}
      <Drawer.Root open={withdrawOpen} onOpenChange={(open) => { setWithdrawOpen(open); if (!open) resetWithdraw() }}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none">
            <div className="bg-surface-elevated rounded-t-3xl border-t border-foreground/10 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-center py-3">
                <div className="w-10 h-1.5 rounded-full bg-foreground/20" />
              </div>

              <div className="px-6 pb-8 safe-area-bottom">
                <AnimatePresence mode="wait">
                  {withdrawStep === 'amount' && (
                    <motion.div
                      key="amount"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                    >
                      <Drawer.Title className="font-display text-2xl font-semibold text-foreground mb-1">提款</Drawer.Title>
                      <p className="text-sm text-muted-foreground mb-2">可用餘額: <span className="text-foreground font-bold">$1,050.00</span></p>
                      <p className="text-xs text-muted-foreground mb-6">選擇或輸入提款金額</p>

                      {/* Preset amounts */}
                      <div className="grid grid-cols-2 gap-2 mb-4">
                        {withdrawPresets.map((preset) => (
                          <button
                            key={preset}
                            onClick={() => setWithdrawAmount(preset)}
                            className={cn(
                              "py-3 rounded-xl font-bold text-sm transition-all",
                              withdrawAmount === preset
                                ? "bg-gold/20 text-gold border border-gold/30"
                                : "glass-card text-foreground hover:border-gold/20"
                            )}
                          >
                            ${(preset / 100).toFixed(0)}
                          </button>
                        ))}
                      </div>

                      <div className="mt-4 p-3 rounded-xl bg-gold/5 border border-gold/20 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-gold flex-shrink-0" />
                        <span className="text-xs text-gold font-medium">提款預計 24 小時內處理完成</span>
                      </div>

                      <button
                        onClick={() => withdrawAmount >= 1000 && setWithdrawStep('confirm')}
                        disabled={withdrawAmount < 1000}
                        className={cn(
                          "w-full h-14 rounded-2xl font-bold text-lg mt-6 transition-all duration-200",
                          withdrawAmount >= 1000
                            ? "btn-primary"
                            : "bg-surface-elevated text-muted-foreground/50 cursor-not-allowed border border-foreground/5"
                        )}
                      >
                        繼續
                      </button>
                    </motion.div>
                  )}

                  {withdrawStep === 'confirm' && (
                    <motion.div
                      key="confirm"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                    >
                      <Drawer.Title className="font-display text-2xl font-semibold text-foreground mb-1">確認提款</Drawer.Title>
                      <p className="text-sm text-muted-foreground mb-6">請確認以下提款資訊</p>

                      <div className="p-4 glass-card rounded-2xl mb-4 space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">提款金額</span>
                          <span className="text-sm font-bold text-foreground">${(withdrawAmount / 100).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">手續費</span>
                          <span className="text-sm font-bold text-foreground">$0.00</span>
                        </div>
                        <div className="border-t border-foreground/5 pt-3 flex justify-between">
                          <span className="text-sm font-bold text-foreground">實際到帳</span>
                          <span className="text-lg font-bold text-gold">${(withdrawAmount / 100).toFixed(2)}</span>
                        </div>
                      </div>

                      <div className="p-3 rounded-xl bg-gold/5 border border-gold/20 flex items-center gap-2 mb-6">
                        <Clock className="w-4 h-4 text-gold flex-shrink-0" />
                        <span className="text-xs text-gold font-medium">預計 24 小時內到帳</span>
                      </div>

                      <div className="flex gap-3">
                        <button
                          onClick={() => setWithdrawStep('amount')}
                          className="flex-1 h-14 glass-card rounded-2xl font-bold text-foreground hover:bg-foreground/5 transition-colors"
                        >
                          返回
                        </button>
                        <button
                          onClick={simulateWithdraw}
                          className="flex-1 h-14 btn-primary rounded-2xl font-bold text-lg"
                        >
                          確認提款
                        </button>
                      </div>
                    </motion.div>
                  )}

                  {withdrawStep === 'processing' && (
                    <motion.div
                      key="processing"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="py-12 text-center"
                    >
                      <Drawer.Title className="sr-only">處理中</Drawer.Title>
                      <div className="w-16 h-16 rounded-full border-4 border-gold/20 border-t-gold animate-spin mx-auto mb-6" />
                      <p className="text-lg font-semibold text-foreground">處理中...</p>
                      <p className="text-sm text-muted-foreground mt-1">請稍候</p>
                    </motion.div>
                  )}

                  {withdrawStep === 'success' && (
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="py-8 text-center"
                    >
                      <Drawer.Title className="sr-only">提款申請已提交</Drawer.Title>
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                        className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6"
                      >
                        <CheckCircle className="w-10 h-10 text-emerald-400" />
                      </motion.div>
                      <p className="text-lg font-semibold text-foreground mb-1">提款申請已提交</p>
                      <p className="text-3xl font-bold text-gold mb-1">${(withdrawAmount / 100).toFixed(2)}</p>
                      <p className="text-sm text-muted-foreground mb-6">預計 24 小時內到帳</p>
                      <button
                        onClick={() => { setWithdrawOpen(false); resetWithdraw() }}
                        className="w-full h-14 btn-primary rounded-2xl font-bold text-lg"
                      >
                        完成
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </motion.div>
  )
}
